import React from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../../api/client";
import type { ApiSuccessResponse } from "../../../api/types";

type PlatformSettings = {
  id: string;
  launchDate: string;
  customerFreeMonthsInitial: number;
  providerFreeMonthsInitial: number;
  customerAdditionalFreeMonths: number;
  commissionRateNewClientSubscriptionModel: number;
  commissionRateNewClientCommissionOnly: number;
  commissionRateReturningClientSubscriptionModel: number;
  commissionRateReturningClientCommissionOnly: number;
  createdAt: string;
  updatedAt: string;
};

function getPlatformSettings() {
  return apiFetch<ApiSuccessResponse<PlatformSettings>>("/platform-settings");
}

export default function PlatformSettingsPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: getPlatformSettings,
  });

  const settings = data?.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <h1 style={{ margin: 0, fontSize: "2rem", color: "#111827" }}>
          Platform settings
        </h1>
        <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.6 }}>
          Review the core platform configuration currently served by the backend.
        </p>
      </div>

      {isLoading && <MessageCard tone="neutral">Loading platform settings...</MessageCard>}

      {isError && (
        <MessageCard tone="error">
          <div>
            Failed to load platform settings:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </div>

          <button type="button" onClick={() => refetch()} style={buttonStyle}>
            Retry
          </button>
        </MessageCard>
      )}

      {!isLoading && !isError && settings && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <SummaryCard settings={settings} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <SectionCard
              title="Launch and access"
              description="Core rollout timing and introductory access settings."
            >
              <InfoRow label="Launch date" value={formatDate(settings.launchDate)} />
              <InfoRow
                label="Initial customer free months"
                value={String(settings.customerFreeMonthsInitial)}
              />
              <InfoRow
                label="Initial provider free months"
                value={String(settings.providerFreeMonthsInitial)}
              />
              <InfoRow
                label="Additional customer free months"
                value={String(settings.customerAdditionalFreeMonths)}
              />
            </SectionCard>

            <SectionCard
              title="New client commission"
              description="Commercial settings used when a provider serves a new client."
            >
              <InfoRow
                label="Subscription model commission"
                value={formatPercent(settings.commissionRateNewClientSubscriptionModel)}
              />
              <InfoRow
                label="Commission-only rate"
                value={formatPercent(settings.commissionRateNewClientCommissionOnly)}
              />
            </SectionCard>

            <SectionCard
              title="Returning client commission"
              description="Commercial settings used for repeat-client activity."
            >
              <InfoRow
                label="Subscription model commission"
                value={formatPercent(
                  settings.commissionRateReturningClientSubscriptionModel,
                )}
              />
              <InfoRow
                label="Commission-only rate"
                value={formatPercent(settings.commissionRateReturningClientCommissionOnly)}
              />
            </SectionCard>

            <SectionCard
              title="Audit"
              description="Record metadata for traceability and administrative review."
            >
              <InfoRow label="Settings ID" value={settings.id} />
              <InfoRow label="Created at" value={formatDateTime(settings.createdAt)} />
              <InfoRow label="Updated at" value={formatDateTime(settings.updatedAt)} />
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ settings }: { settings: PlatformSettings }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "16px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <span
          style={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Core configuration
        </span>

        <h2 style={{ margin: 0, fontSize: "1.35rem", color: "#111827" }}>
          Platform launch and commercial settings
        </h2>

        <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.6 }}>
          Launch date {formatDate(settings.launchDate)} · Initial customer free period{" "}
          {settings.customerFreeMonthsInitial} months · New client subscription commission{" "}
          {formatPercent(settings.commissionRateNewClientSubscriptionModel)}
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        <Pill label={`Launch: ${formatDate(settings.launchDate)}`} />
        <Pill label={`Customer free months: ${settings.customerFreeMonthsInitial}`} />
        <Pill label={`Provider free months: ${settings.providerFreeMonthsInitial}`} />
      </div>
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "16px",
        padding: "18px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <h3 style={{ margin: 0, fontSize: "1rem", color: "#111827" }}>{title}</h3>
        <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.5, fontSize: "0.95rem" }}>
          {description}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>{children}</div>
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        paddingBottom: "12px",
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <span style={{ fontSize: "0.86rem", fontWeight: 600, color: "#64748b" }}>
        {label}
      </span>
      <span style={{ color: "#111827", lineHeight: 1.6, wordBreak: "break-word" }}>
        {value}
      </span>
    </div>
  );
}

function MessageCard({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "neutral" | "error";
}) {
  const isError = tone === "error";

  return (
    <div
      style={{
        background: isError ? "#fef2f2" : "#ffffff",
        border: `1px solid ${isError ? "#fecaca" : "#e5e7eb"}`,
        borderRadius: "14px",
        padding: "18px",
        color: isError ? "#b91c1c" : "#4b5563",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {children}
    </div>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "8px 12px",
        borderRadius: "999px",
        background: "#eef2ff",
        color: "#4338ca",
        fontSize: "0.9rem",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function formatPercent(value: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value?: string) {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value?: string) {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

const buttonStyle: React.CSSProperties = {
  alignSelf: "flex-start",
  background: "#111827",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 600,
};